import { Option, ReadonlyArray } from "@morphism/fp";
import React from "react";

import { Asset } from "~/Asset";
import { DVIRs } from "~/DVIR";
import { ID } from "~/ID";
import * as Log from "~/Log";
import { Mutable } from "~/Mutable";
import { NotApplicable } from "~/NotApplicable";
import { Order } from "~/Order";
import { Select as GenericSelect } from "~/Select";

export function Vehicle({ summary }: Log.Summary) {
  return pipe(
    summary.shift?.assets ?? [],
    ReadonlyArray.sort(Asset.order()),
    ReadonlyArray.map(({ asset }) => (
      <Asset.Link key={asset.id} noLink asset={asset} />
    )),
    ([first, ...rest]) => <>{first ? [first, ...rest] : <NotApplicable />}</>
  );
}

export namespace Vehicle {
  export const formatStringForExport = ({ asset }: Asset, includeLink = false) =>
    // asset obj with custom name => 'assetID / assetNAME (assetLINK)'
    // asset obj with NO custom name => 'assetID (assetLINK)'
    asset.name
      ? `${asset.id} / ${
          asset.name
        }${includeLink ? ` (${Asset.useHREF({ asset })})` : ''}`
      : asset.id;

  export const column = ({
    summaries,
    filter,
    onFilterChange,
  }: Log.Summaries & {
    readonly filter?: readonly ID[];
    readonly onFilterChange: (assets?: readonly ID[]) => void;
  }) => ({
    title: "Vehicle",
    dataIndex: "vehicle",
    filteredValue: filter && ["vehicle"],

    filterDropdown: (props: { readonly confirm: () => void }) => (
      <Filter {...{ summaries, filter, onFilterChange }} {...props} />
    ),

    onFilter: (_: unknown, { summary }: Log.Summary) => {
      const ids = (filter ?? []).map(({ id }) => id);
      return !ids[0]
        ? true
        : pipe(
            summary.shift?.assets ?? [],
            ReadonlyArray.findFirst(({ asset }) => ids.includes(asset.id)),
            Option.isSome
          );
    },

    render: (_: unknown, summary: Log.Summary) => <Vehicle {...summary} />,
    sorter: (a: Log.Summary, b: Log.Summary): Order.Ordering =>
      Order.optionally(Asset.order().compare)(
        a.summary.shift?.assets[0],
        b.summary.shift?.assets[0]
      ),
  });

  export const Filter = ({
    summaries,
    filter,
    onFilterChange,
    confirm,
  }: DVIRs.Table.Filter.Props & Parameters<typeof column>[0]) => {
    const selected = (filter ?? []).map(({ id }) => id);
    const options = pipe(
      summaries,
      ReadonlyArray.chain(
        flow(
          ({ summary }) => summary.shift?.assets ?? [],
          ReadonlyArray.sort(Asset.order()),
          ReadonlyArray.map(({ asset }) => ({
            key: asset.id,
            value: Asset.name({ asset }),
          }))
        )
      ),
      ReadonlyArray.uniq({ equals: ({ key: a }, { key: b }) => a === b })
    );

    const value = pipe(
      options,
      ReadonlyArray.filterMap(({ key, value }) =>
        selected.includes(key) ? Option.some(value) : Option.none
      )
    );

    return (
      <DVIRs.Table.Filter confirm={confirm} onReset={onFilterChange}>
        <GenericSelect
          style={{ width: 200 }}
          dropdownMatchSelectWidth
          placeholder={`Filter by vehicle`}
          mode="multiple"
          bordered={false}
          options={options as Mutable<typeof options>}
          value={value as Mutable<typeof value>}
          onChange={
            ((_value: never, selected: readonly { readonly key: string }[]) =>
              onFilterChange(selected.map(({ key: id }) => ({ id })))) as never
          }
        />
      </DVIRs.Table.Filter>
    );
  };
}
